import { useNavigate } from 'react-router-dom';
import styles from './HomePage.module.css';
import Navigation from './Navigation';
import { useInlineTranslation } from '../../services/translationService';


const Button = ({ children, className = '', onClick }) => (
  <button
    className={`${styles.button} ${className}`}
    onClick={onClick}
    aria-label={typeof children === 'string' ? children : undefined}
  >
    {children}
  </button>
);

const Card = ({ children, className = '' }) => (
  <div className={`${styles.card} ${className}`} role="region">
    {children}
  </div>
);

const HomePage = () => {
  const navigate = useNavigate();
  
  // Hero Section
  const heroTitle = useInlineTranslation(
    "Stop Spending Hours on One Video",
    "Arrêtez de Passer des Heures sur Une Vidéo"
  );
  const heroDescription = useInlineTranslation(
    "Create professional video content in 30 minutes - Complete 9-step workflow from idea to published without recording.",
    "Créez du contenu vidéo professionnel en 30 minutes - Flux de travail complet en 9 étapes de l'idée à la publication sans enregistrement."
  );
  const heroCTA = useInlineTranslation(
    "Start Your 30-Minute Workflow →",
    "Démarrez Votre Flux de 30 Minutes →"
  );

  // Script Generator
  const scriptTitle = useInlineTranslation("AI Script Generator", "Générateur de Scripts IA");
  const scriptDesc = useInlineTranslation(
    "Transform your ideas into complete scripts in 5 minutes - the foundation of your content",
    "Transformez vos idées en scripts complets en 5 minutes - la base de votre contenu"
  );
  const scriptCTA = useInlineTranslation("Start with Script →", "Commencer avec Script →");

  // Prompt Generator
  const promptTitle = useInlineTranslation("AI Prompt Generator", "Générateur de Prompts IA");
  const promptDesc = useInlineTranslation(
    "Convert your script into optimized video prompts for stunning AI-generated visuals",
    "Convertissez votre script en prompts vidéo optimisés pour des visuels IA époustouflants"
  );
  const promptCTA = useInlineTranslation("Generate Video Prompts →", "Générer des Prompts Vidéo →");

  // Audio Generator
  const audioTitle = useInlineTranslation("AI Audio Generator", "Générateur Audio IA");
  const audioDesc = useInlineTranslation(
    "Create professional narration and voiceover from your script - no recording needed",
    "Créez une narration professionnelle à partir de votre script - aucun enregistrement nécessaire"
  );
  const audioCTA = useInlineTranslation("Add Professional Voice →", "Ajouter une Voix Professionnelle →");

  // Content Studio
  const studioTitle = useInlineTranslation("AI Content Studio", "Studio de Contenu IA");
  const studioDesc = useInlineTranslation(
    "Transform your audio into engaging video content with our all-in-one AI studio",
    "Transformez votre audio en contenu vidéo captivant avec notre studio IA tout-en-un"
  );
  const studioCTA = useInlineTranslation("Transform Audio to Video →", "Transformer Audio en Vidéo →");

  // Video Editor
  const editorTitle = useInlineTranslation("AI Video Editor", "Éditeur Vidéo IA");
  const editorDesc = useInlineTranslation(
    "Edit and enhance your videos without audio using advanced AI technology",
    "Modifiez et améliorez vos vidéos sans audio grâce à la technologie IA avancée"
  );
  const editorCTA = useInlineTranslation("Edit Your Videos →", "Modifier Vos Vidéos →");

  // Video Generator
  const videoTitle = useInlineTranslation("AI Video Generator", "Générateur de Vidéos IA");
  const videoDesc = useInlineTranslation(
    "Generate complete videos using your prompts and audio - your visual content studio",
    "Générez des vidéos complètes avec vos prompts et audio - votre studio de contenu visuel"
  );
  const videoCTA = useInlineTranslation("Create Video →", "Créer une Vidéo →");

  // Caption Generator
  const captionTitle = useInlineTranslation("Caption & Text Generator", "Générateur de Sous-titres & Texte");
  const captionDesc = useInlineTranslation(
    "Add professional captions and custom text overlays to boost engagement by 80%",
    "Ajoutez des sous-titres professionnels et du texte personnalisé pour augmenter l'engagement de 80%"
  );
  const captionCTA = useInlineTranslation("Add Captions & Text →", "Ajouter Sous-titres & Texte →");

  // Soundtrack
  const soundtrackTitle = useInlineTranslation("Soundtrack & Effects", "Musiques & Effets");
  const soundtrackDesc = useInlineTranslation(
    "Enhance your video with copyright-free background music and sound effects",
    "Améliorez votre vidéo avec de la musique de fond et des effets sonores libres de droits"
  );
  const soundtrackCTA = useInlineTranslation("Add Audio Elements →", "Ajouter Éléments Audio →");

  // SEO Titles
  const seoTitle = useInlineTranslation("SEO Titles & Descriptions", "Titres & Descriptions SEO");
  const seoDesc = useInlineTranslation(
    "Generate click-worthy titles and descriptions optimized for maximum views",
    "Générez des titres et descriptions percutants optimisés pour un maximum de vues"
  );
  const seoCTA = useInlineTranslation("Optimize for Discovery →", "Optimiser pour la Découverte →");

  // Thumbnail Creator
  const thumbnailTitle = useInlineTranslation("AI Thumbnail Creator", "Créateur de Miniatures IA");
  const thumbnailDesc = useInlineTranslation(
    "Create eye-catching thumbnails that increase click-through rates by 90%",
    "Créez des miniatures accrocheuses qui augmentent les taux de clic de 90%"
  );
  const thumbnailCTA = useInlineTranslation("Create Thumbnails →", "Créer des Miniatures →");

  // Publishing
  const publishTitle = useInlineTranslation("One-Click Publishing", "Publication en Un Clic");
  const publishDesc = useInlineTranslation(
    "Publish your complete video to YouTube, Instagram, and Facebook simultaneously",
    "Publiez votre vidéo complète sur YouTube, Instagram et Facebook simultanément"
  );
  const publishCTA = useInlineTranslation("Publish Everywhere →", "Publier Partout →");

  return (
    <div className={styles.container}>
      {/* Header with Navigation */}
      <Navigation></Navigation>

      <main>
        {/* Demo & Call-to-Action Section */}
        <section className={styles.demoCTA}>
          {/* VIDEO SECTION - COMMENTED OUT UNTIL WE HAVE A DEMO VIDEO */}
          {/* <div className={styles.videoContainer}>
            <video controls className={styles.demoVideo}>
              <source src="/api/demo-video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div> */}
          <div className={styles.ctaContent}>
            <h1>{heroTitle}</h1>
            <p>{heroDescription}</p>
            <Button onClick={() => { navigate("/script-generator") }}>
              {heroCTA}
            </Button>
          </div>
        </section>

        {/* Features Section */}
        <section className={styles.features}>
          {/* AI Script Generator */}
          <Card>
            <div className={styles.iconPlaceholder}>📜</div>
            <h2>{scriptTitle}</h2>
            <p>{scriptDesc}</p>
            <Button onClick={() => { navigate("/script-generator") }}>
              {scriptCTA}
            </Button>
          </Card>

          {/* AI Prompt Generator */}
          <Card>
            <div className={styles.iconPlaceholder}>🎯</div>
            <h2>{promptTitle}</h2>
            <p>{promptDesc}</p>
            <Button onClick={() => { navigate("/prompt-generator") }}>
              {promptCTA}
            </Button>
          </Card>

          {/* AI Audio/Voiceover Generator */}
          <Card>
            <div className={styles.iconPlaceholder}>🎵</div>
            <h2>{audioTitle}</h2>
            <p>{audioDesc}</p>
            <Button onClick={() => { navigate("/audio-generation") }}>
              {audioCTA}
            </Button>
          </Card>

          {/* NEW: AI Content Studio */}
          <Card>
            <div className={styles.iconPlaceholder}>✨</div>
            <h2>{studioTitle}</h2>
            <p>{studioDesc}</p>
            <Button onClick={() => { navigate("/create-content") }}>
              {studioCTA}
            </Button>
          </Card>

          {/* NEW: AI Video Editor */}
          <Card>
            <div className={styles.iconPlaceholder}>🎞️</div>
            <h2>{editorTitle}</h2>
            <p>{editorDesc}</p>
            <Button onClick={() => { navigate("/ai-video-editor") }}>
              {editorCTA}
            </Button>
          </Card>

          {/* AI Video Generator */}
          <Card>
            <div className={styles.iconPlaceholder}>🎬</div>
            <h2>{videoTitle}</h2>
            <p>{videoDesc}</p>
            <Button onClick={() => { navigate("/create-content") }}>
              {videoCTA}
            </Button>
          </Card>

          {/* Auto Caption & Text Generator */}
          <Card>
            <div className={styles.iconPlaceholder}>📝</div>
            <h2>{captionTitle}</h2>
            <p>{captionDesc}</p>
            <Button onClick={() => { navigate("/Caption-LandingPage") }}>
              {captionCTA}
            </Button>
          </Card>

          {/* AI Soundtrack & Effects */}
          <Card>
            <div className={styles.iconPlaceholder}>🎵</div>
            <h2>{soundtrackTitle}</h2>
            <p>{soundtrackDesc}</p>
            <Button onClick={() => { navigate("/sound-generation") }}>
              {soundtrackCTA}
            </Button>
          </Card>

          {/* SEO Titles & Descriptions */}
          <Card>
            <div className={styles.iconPlaceholder}>✍️</div>
            <h2>{seoTitle}</h2>
            <p>{seoDesc}</p>
            <Button onClick={() => { navigate("/title-landing") }}>
              {seoCTA}
            </Button>
          </Card>

          {/* AI Thumbnail Generator */}
          <Card>
            <div className={styles.iconPlaceholder}>🖼️</div>
            <h2>{thumbnailTitle}</h2>
            <p>{thumbnailDesc}</p>
            <Button onClick={() => { navigate("/thumbnail-landing") }}>
              {thumbnailCTA}
            </Button>
          </Card>

          {/* Multi-Platform Publishing */}
          <Card>
            <div className={styles.iconPlaceholder}>📤</div>
            <h2>{publishTitle}</h2>
            <p>{publishDesc}</p>
            <Button onClick={() => { navigate("/social-media") }}>
              {publishCTA}
            </Button>
          </Card>
        </section>
      </main>
    </div>
  );
};

export default HomePage;
